// Platform detection utilities for messaging app recommendations

export type Platform = 'macos' | 'windows' | 'linux' | 'ios' | 'android' | 'unknown';

export type MessagingApp = 'apple_messages' | 'google_messages' | 'google_voice' | 'other';

interface PlatformInfo {
  platform: Platform;
  isMobile: boolean;
  recommendedMessagingApp: MessagingApp;
  messagingAppLabel: string;
}

export function detectPlatform(): PlatformInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  // Mobile detection
  const isMobile = /iphone|ipad|ipod|android/.test(userAgent);

  // iOS
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return {
      platform: 'ios',
      isMobile: true,
      recommendedMessagingApp: 'apple_messages',
      messagingAppLabel: 'Apple Messages',
    };
  }

  // Android
  if (/android/.test(userAgent)) {
    return {
      platform: 'android',
      isMobile: true,
      recommendedMessagingApp: 'google_messages',
      messagingAppLabel: 'Google Messages',
    };
  }

  // macOS
  if (platform.includes('mac') || /macintosh/.test(userAgent)) {
    return {
      platform: 'macos',
      isMobile: false,
      recommendedMessagingApp: 'apple_messages',
      messagingAppLabel: 'Apple Messages',
    };
  }

  // Windows
  if (platform.includes('win') || /windows/.test(userAgent)) {
    return {
      platform: 'windows',
      isMobile: false,
      // Windows users most likely use Google Voice or Android Messages for Web
      recommendedMessagingApp: 'google_voice',
      messagingAppLabel: 'Google Voice',
    };
  }

  // Linux
  if (platform.includes('linux')) {
    return {
      platform: 'linux',
      isMobile: false,
      recommendedMessagingApp: 'google_voice',
      messagingAppLabel: 'Google Voice',
    };
  }

  return {
    platform: 'unknown',
    isMobile,
    recommendedMessagingApp: 'other',
    messagingAppLabel: 'Other',
  };
}

// Generate appropriate message link based on messaging app preference
export function getMessageLink(phone: string, messagingApp: MessagingApp): string {
  // Clean phone number - remove non-digits except leading +
  const cleanPhone = phone.replace(/[^\d+]/g, '');

  switch (messagingApp) {
    case 'apple_messages':
      // sms: scheme works on macOS and iOS
      return `sms:${cleanPhone}`;

    case 'google_messages':
      // Open Google Messages for Web
      return `https://messages.google.com/web/conversations`;

    case 'google_voice':
      // Open Google Voice web interface
      return `https://voice.google.com`;

    case 'other':
    default:
      // Fall back to sms: scheme which most platforms handle
      return `sms:${cleanPhone}`;
  }
}

// Get display info for messaging app options
export function getMessagingAppOptions() {
  return [
    {
      value: 'apple_messages',
      label: 'Apple Messages',
      description: 'iMessage & SMS on macOS/iOS',
      platforms: ['macos', 'ios'] as Platform[],
    },
    {
      value: 'google_messages',
      label: 'Google Messages',
      description: 'messages.google.com (pairs with Android)',
      platforms: ['windows', 'linux', 'android'] as Platform[],
    },
    {
      value: 'google_voice',
      label: 'Google Voice',
      description: 'voice.google.com',
      platforms: ['windows', 'linux', 'macos'] as Platform[],
    },
    {
      value: 'other',
      label: 'Other / Manual Only',
      description: 'Log messages manually',
      platforms: ['macos', 'windows', 'linux', 'ios', 'android', 'unknown'] as Platform[],
    },
  ];
}
